import { get } from "lodash";
import cx from "classnames";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { fetchDealRoomActiveChatUser, fetchingDealRoomEngageChatUserSucceeded } from "src/redux/actions/dealRoom";
import ChatSection from "src/pages/dealRoom/components/chat/chatContainer/chatSection";
import ChatInput from "src/pages/dealRoom/components/chat/chatContainer/mentions/components/ChatInput";
import UserDetails, { PlaceholderUserDetails } from "../userDetails";
import AtMentionPopover from "src/pages/dealRoom/components/chat/chatContainer/mentions/components/AtMentionPopover";

import { sendMessage } from "src/legacy/redux/actions/smallTalk";

import { AppContext } from "src/components/app/contexts/appContext";
import { DealRoomContext } from "../../context";
import { guid } from "src/utils/util";

import {
  AtMentionSearchTab,
  DEFAULT_AT_MENTION_SEARCH_TAB,
  getAtMentionSearchApiType,
  getMentionTypeForAtMentionTab,
  Suggestion,
  SuggestionSearchResult,
  TriggerConfig,
} from "src/pages/dealRoom/components/chat/chatContainer/mentions/types";

import chatInputStyles from "./mentions/components/ChatInput/styles.module.scss"
import styles from "./styles.module.scss";
import { useDealRoomMentionsSearch } from "src/query/settings/dealRoomMentionsSearch";
import { extractMentionsFromMessage } from "src/pages/dealRoom/components/chat/chatContainer/mentions/utils/parser";
import { extractDealRoomSearchEntity } from "src/pages/dealRoom/components/chat/chatContainer/mentions/utils/parser";

const suggestionFromExtracted = (
  fields: {
    id: string;
    name: string;
    iconThumbnail?: string;
    peopleMentionKind?: "user" | "contact";
  },
  tab: AtMentionSearchTab
): Suggestion => ({
  id: fields.id,
  label: fields.name,
  group: tab,
  mentionType: getMentionTypeForAtMentionTab(tab),
  imgSrc: fields.iconThumbnail ?? "",
  ...(tab === "people" && fields.peopleMentionKind
    ? { serializedMentionType: fields.peopleMentionKind }
    : {}),
});

interface ChatContainerProps {
  totalParticipants?: number
}

const ChatContainer: React.FC<ChatContainerProps> = ({ totalParticipants }) => {
  const dispatch = useDispatch();
  const { conversationId } = useParams();
  const chatUserId = conversationId ?? '';
  const { smallTalk } = useContext(AppContext);
  const { dealRoom, everyoneChannel } = useContext(DealRoomContext);
  const dealRoomId = get(dealRoom, 'id', '');
  const canEdit = get(dealRoom, 'canEdit', false);
  const isViewOnly = !canEdit;

  const [atMentionTab, setAtMentionTab] = useState<AtMentionSearchTab>(
    DEFAULT_AT_MENTION_SEARCH_TAB
  );

  const { mutateAsync: searchDealRoomMentions, isPending } = useDealRoomMentionsSearch();

  const triggers: TriggerConfig[] = useMemo(
    () => [
      {
        char: "@",
        PopoverComponent: AtMentionPopover,
        isLoading: isPending,
        getSuggestions: async (query, meta) => {
          if (!dealRoomId) {
            return [];
          }
          const tab = meta?.tab ?? DEFAULT_AT_MENTION_SEARCH_TAB;
          const page = meta?.page ?? 0;
          const limit = meta?.limit ?? 20;
          const apiType = getAtMentionSearchApiType(tab);
          const { items = [], pagination } = await searchDealRoomMentions({
            id: dealRoomId,
            query,
            page,
            limit,
            type: apiType,
          });
          const out: Suggestion[] = [];
          for (const raw of items) {
            const fields = extractDealRoomSearchEntity(raw);
            if (fields) out.push(suggestionFromExtracted(fields, tab));
          }
          const result: SuggestionSearchResult = {
            items: out,
            pagination,
            hasMore: pagination
              ? page + 1 < pagination.totalPagesCount
              : out.length >= limit,
          };
          return result;
        },
        createMentionNode: (item) => {
          const fragment = document.createDocumentFragment();

          const span = document.createElement("span");
          span.contentEditable = "false";
          span.dataset.identifier = item.id;
          span.dataset.mentionType =
            item.serializedMentionType ?? item.mentionType ?? "";

          span.appendChild(document.createTextNode(`@${item.label}`));

          span.classList.add(chatInputStyles.mention);

          fragment.appendChild(span);

          const emptySpan = document.createElement("span");
          fragment.appendChild(emptySpan);

          const space = document.createTextNode("\u00A0");
          fragment.appendChild(space);

          return fragment;
        },
      }
    ],
    [dealRoomId, isPending, searchDealRoomMentions]
  );

  const chatUserData = useSelector((state: any) => {
    return get(state, `dealRooms.dealRoom.${dealRoomId}.engage.activeChatUser`, null);
  });
  const activeChatUser = get(chatUserData, 'item', {});
  const chatUserLoading = get(chatUserData, 'loading', false);

  const channelName = get(activeChatUser, 'uuid', []);

  const me = useSelector((state: any) => {
    return get(state, 'users.me', null);
  });
  const myUuid = get(me, 'id', '');

  const handleSendMessage = (messageText: string) => {

    const mentions = extractMentionsFromMessage(messageText);

    const request = {
      channel: channelName[0],
      message: {
        id: guid(),
        type: "MESSAGE",
        text: messageText,
        timestamp: new Date().getTime(),
        fingerPrint: "",
        sharedUrl: '',
        sender: {
          chatUser: get(activeChatUser, 'id', ''),
          id: me.id,
          name: `${get(me, 'firstName') || ''} ${get(me, 'lastName') || ''}`,
          type: 'user'
        },
        mentions,
        dealRoomId,
      },
      uuid: myUuid,
    }

    dispatch(sendMessage(smallTalk.client, request) as any);
  }

  useEffect(() => {
    if (chatUserId === everyoneChannel.id) {
      dispatch(fetchingDealRoomEngageChatUserSucceeded({
        response: everyoneChannel,
        dealRoomId
      }));
    } else {
      dispatch(fetchDealRoomActiveChatUser({
        dealRoomId,
        chatUserId
      }));
    }
  }, [chatUserId]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {
          !chatUserLoading ? (
            <UserDetails showMembersCount={true} totalParticipants={totalParticipants} user={activeChatUser} showHighlights={false} />
          ) : (
            <div className={styles.loadingContainer}>
              <PlaceholderUserDetails />
            </div>
          )
        }
      </div>

      <div className={styles.divider}></div>

      <ChatSection />

      {!isViewOnly && (
        <ChatInput
          triggers={triggers}
          onSubmit={handleSendMessage}
          atMentionTab={atMentionTab}
          onAtMentionTabChange={setAtMentionTab}
          maskConfig={isViewOnly}
      />)}
      
      {isViewOnly && (
        <div className={styles.blockedMask} aria-live="polite">
          <span className={styles.blockedMaskHeader}>
            <i className={cx("paperflite-eye", styles.blockedMaskIcon)} />
            <span className={styles.blockedMaskTitle}>View only</span>
          </span>
          <div className={styles.blockedMaskMessage}>
            <span>You have view-only access to this<br /> Deal Room.</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatContainer;